import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/authContext';
import { usePermissions } from '../../../../contexts/PermissionsContext';
import { getUserHighestRole } from '../../../../utils/roleHelpers';
import supabase from '../../../../supabaseClient';
import UnifiedSidebar from '../../../../components/UnifiedSidebar';
import '../../../Sidebar.css';

// Role-based navigation config (unchanged)
const roleSidebarNav = {
  superadmin: [
    {
      section: 'Navigation',
      items: [
        { to: '.', label: 'Main', icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
        ), end: true },
        { to: 'dashboard', label: 'Dashboard', icon: '📊' },
        { to: 'notifications', label: 'Notifications', icon: '👥' },
        { to: 'courses', label: 'Course Management', icon: '📚' },
        { to: 'analytics', label: 'Analytics', icon: '📈' },
        { to: 'reports', label: 'Reports', icon: '📑' },
        { to: 'moderation', label: 'Moderation', icon: '🛡️' },
      ]
    },
    {
      section: 'System',
      items: [
        { to: 'storage', label: 'Storage', icon: '💾' },
        { to: 'integrations', label: 'Integrations', icon: '🔗' },
        { to: 'settings', label: 'Settings', icon: '⚙️' },
        { to: 'system', label: 'System', icon: '🖥️', badge: 'systemAlerts' },
        { to: 'roles', label: 'Roles', icon: '🛡️', badge: 'roleRequests' },
        { to: 'logs', label: 'Logs', icon: '📝' },
      ]
    },
    {
      section: 'Support',
      items: [
        { to: 'help', label: 'Help', icon: '❓' },
      ]
    }
  ],
  admin: [
    {
      section: 'Navigation',
      items: [
        { to: '/app/academy/lms/admin', label: 'Main', icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
        ), end: true },
        { to: 'dashboard', label: 'Dashboard', icon: '📊' },
        { to: 'notifications', label: 'Notifications', icon: '👥' },
        { to: 'courses', label: 'Course Management', icon: '📚' },
        { to: '/app/academy/lms/admin/reports', label: 'Reports', icon: '📑' },
        { to: 'analytics', label: 'Analytics', icon: '📈' },
        { to: 'moderation', label: 'Moderation', icon: '🛡️' },
      ]
    },
    {
      section: 'System',
      items: [
        { to: 'settings', label: 'Settings', icon: '⚙️' },
        { to: '/app/academy/lms/admin/system', label: 'System', icon: '🖥️' },
        { to: 'logs', label: 'Logs', icon: '📝' },
      ]
    },
    {
      section: 'Support',
      items: [
        { to: '/app/academy/lms/admin/help', label: 'Help', icon: '❓' },
      ]
    }
  ],
  manager: [
    {
      section: 'Navigation',
      items: [
        { to: '.', label: 'Main', icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
        ), end: true },
        { to: 'dashboard', label: 'Dashboard', icon: '📊' },
        { to: 'courses', label: 'Course Management', icon: '📚' },
        { to: 'assessments', label: 'Assessments', icon: '📝' },
        { to: 'teachers', label: 'Teachers', icon: '👩‍🏫' },
        { to: 'students', label: 'Students', icon: '🧑‍🎓' },
        { to: 'groups', label: 'Groups', icon: '👥' },
        { to: 'reports', label: 'Reports', icon: '📑' },
      ]
    },
    {
      section: 'Support',
      items: [
        { to: 'help', label: 'Help', icon: '❓' },
      ]
    }
  ],
  designer: [
    {
      section: 'Navigation',
      items: [
        { to: '.', label: 'Main', icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
        ), end: true },
        { to: 'courses', label: 'Course Builder Studio', icon: '🎨' },
        { to: 'resources', label: 'Resources', icon: '🗂️', badge: 'newResources' },
        { to: 'designs', label: 'Designs', icon: '📐', badge: 'pendingDesigns' },
      ]
    },
    {
      section: 'Support',
      items: [
        { to: 'help', label: 'Help', icon: '❓' },
      ]
    }
  ],
  teacher: [
    {
      section: 'Navigation',
      items: [
        { to: '.', label: 'My Courses', icon: '📚', end: true },
        { to: 'courses', label: 'Course Management', icon: '⚙️' },
        { to: 'reports', label: 'Reports', icon: '📑' },
        { to: 'analytics', label: 'Analytics', icon: '📊' },
      ]
    },
    {
      section: 'Support',
      items: [
        { to: 'help', label: 'Help', icon: '❓' },
      ]
    }
  ],
  student: [
    {
      section: 'Navigation',
      items: [
        { to: '.', label: 'My Courses', icon: '📚', end: true },
        { to: 'reports', label: 'Reports', icon: '📑' },
        { to: 'analytics', label: 'Analytics', icon: '📊' },
      ]
    },
    {
      section: 'Support',
      items: [
        { to: 'help', label: 'Help', icon: '❓' },
      ]
    }
  ]
};

const LmsSidebar = ({ onToggleSidebar = () => {} }) => {
  const { user } = useAuth();
  const { roles, activeRole, setActiveRole, isSuperadmin, ALL_ROLES } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract role section from current path (e.g., /app/academy/lms/manager/...)
  const pathMatch = location.pathname.match(/\/lms\/(\w+)/);
  const routeRole = pathMatch ? pathMatch[1] : null;
  // Only allow valid roles
  const validRoles = ['superadmin', 'admin', 'manager', 'designer', 'teacher', 'student'];
  
  // Determine sidebar role with priority:
  // 1. Use route role if explicitly in URL path
  // 2. Use activeRole if set (important for base LMS route /app/academy/lms)
  // 3. Fall back to highest user role
  const sidebarRole = routeRole && validRoles.includes(routeRole)
    ? routeRole
    : (activeRole && validRoles.includes(activeRole)
        ? activeRole
        : (roles && roles.length > 0 ? getUserHighestRole(roles) : 'superadmin'));

  // Sync activeRole with URL changes when navigating to different role sections
  useEffect(() => {
    if (routeRole && validRoles.includes(routeRole) && routeRole !== activeRole) {
      // Only update if the user actually has this role or if superadmin is impersonating
      const hasRole = roles?.some(r => r.role === routeRole) || isSuperadmin;
      if (hasRole) {
        console.log('[LmsSidebar] Syncing activeRole with URL:', { routeRole, prevActiveRole: activeRole });
        setActiveRole(routeRole);
      }
    }
  }, [location.pathname, routeRole, activeRole, roles, isSuperadmin, setActiveRole]);

  // Debug output for sidebar state
  console.log('[LmsSidebar] Render:', {
    location: location.pathname,
    roles,
    routeRole,
    activeRole,
    sidebarRole
  });

  // When switching roles via dropdown, update the route
  const handleRoleChange = (newRole) => {
    console.log('[LmsSidebar] handleRoleChange:', { newRole, prevActiveRole: activeRole });
    setActiveRole(newRole);
    // Find the main route for the new role (either relative '.' or absolute path)
    const mainItem = (roleSidebarNav[newRole] || []).flatMap(section => section.items).find(item => 
      item.label === 'Main' || item.to === '.' || item.to.includes(`/app/academy/lms/${newRole}`)
    );
    
    let mainRoute;
    if (mainItem) {
      // If it's an absolute path, use it directly
      if (mainItem.to.startsWith('/app/academy/lms/')) {
        mainRoute = mainItem.to;
      } else if (mainItem.to === '.') {
        // If it's a relative path, construct the absolute path
        mainRoute = `/app/academy/lms/${newRole}`;
      } else {
        // Fallback: construct from role
        mainRoute = `/app/academy/lms/${newRole}`;
      }
    } else {
      // Fallback to dashboard if no main item found
      mainRoute = `/app/academy/lms/${newRole}/dashboard`;
    }
    
    console.log('[LmsSidebar] Navigating to:', mainRoute);
    navigate(mainRoute);
  };


  // Badge state
  const [systemAlerts, setSystemAlerts] = useState(0);
  const [roleRequests, setRoleRequests] = useState(0);
  // Designer analytics badge state
  const [pendingDesigns, setPendingDesigns] = useState(0);
  const [newResources, setNewResources] = useState(0);

  // Role switcher options
  const assignedRoleOptions = roles.map(r => ({ value: r.role, label: r.label || r.role }));
  const impersonateOptions = isSuperadmin
    ? ALL_ROLES.filter(r => !roles.some(ar => ar.role === r.key)).map(r => ({ value: r.key, label: `[Impersonate] ${r.label}` }))
    : [];
  const allOptions = [...assignedRoleOptions, ...impersonateOptions];

  // Build menuSections for UnifiedSidebar
  const navConfig = roleSidebarNav[sidebarRole] || [];
  const menuSections = navConfig.map(section => ({
    section: section.section,
    items: section.items.map(item => ({
      id: item.to,
      label: item.label,
      icon: item.icon,
      badge: item.badge,
      onClick: () => {
        // Navigation logic
        const basePath = `/app/academy/lms/${sidebarRole}`;
        if (item.to === '.') {
          navigate(basePath);
        } else if (item.to.startsWith('/')) {
          // Handle absolute paths directly
          navigate(item.to);
        } else {
          // Handle relative paths
          navigate(`${basePath}/${item.to}`);
        }
      },
    }))
  }));

  // Home button handler
  const handleHome = () => navigate('/app/home');

  // Logout handler
  const handleLogout = () => {
    window.location.href = '/';
  };

  // Badge map
  const badges = {
    systemAlerts,
    roleRequests,
    pendingDesigns,
    newResources,
  };

  // Fetch badge counts for superadmin and designer
  useEffect(() => {
    if (sidebarRole === 'superadmin') {
      supabase.rpc('count_system_alerts')
        .then(({ data, error }) => {
          if (!error) setSystemAlerts(data || 0);
          else setSystemAlerts(0);
        })
        .catch(() => setSystemAlerts(0));
      supabase.rpc('count_role_requests')
        .then(({ data, error }) => {
          if (!error) setRoleRequests(data || 0);
          else setRoleRequests(0);
        })
        .catch(() => setRoleRequests(0));
    } else if (sidebarRole === 'designer') {
      supabase.rpc('count_pending_designs')
        .then(({ data, error }) => {
          if (!error) setPendingDesigns(data || 0);
          else setPendingDesigns(0);
        })
        .catch(() => setPendingDesigns(0));
      supabase.rpc('count_new_resources')
        .then(({ data, error }) => {
          if (!error) setNewResources(data || 0);
          else setNewResources(0);
        })
        .catch(() => setNewResources(0));
    }
  }, [sidebarRole]);

  // Debug output for render return
  console.log('[LmsSidebar] Returning sidebar with:', {
    menuSections,
    sidebarRole,
    activeRole,
    location: location.pathname
  });

  return (
    <UnifiedSidebar
      menuSections={menuSections}
      showRoleSwitcher={true}
      roleOptions={allOptions}
      activeRole={activeRole}
      onRoleChange={handleRoleChange}
      onHomeClick={handleHome}
      onLogout={handleLogout}
      badges={badges}
      logoText="VirtuLingu"
      showHome={true}
      activeSection={location.pathname.split('/').pop()}
      dense={true}
      onCollapseChange={onToggleSidebar}
    />
  );
};

export default LmsSidebar;
